import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { LINE_HEIGHT } from '../link';
import { LOCALIZED_STRINGS } from './constants';

export class CollapsibleText extends React.PureComponent {
    static propTypes = {
        children: PropTypes.string.isRequired,
        collapseWords: PropTypes.number,
        maxWords: PropTypes.number,
        onClickShowMore: PropTypes.func,
        showLessEnabled: PropTypes.bool,
        showMoreHelperText: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                id: PropTypes.string,
                defaultMessage: PropTypes.string,
            }),
        ]),
        showLessHelperText: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                id: PropTypes.string,
                defaultMessage: PropTypes.string,
            }),
        ]),
    };

    static defaultProps = {
        collapseWords: 40,
        maxWords: 50,
        onClickShowMore: noop,
        showLessEnabled: true,
        showMoreHelperText: LOCALIZED_STRINGS.ShowMore,
        showLessHelperText: LOCALIZED_STRINGS.ShowLess,
    };

    state = {
        collapse: true,
    };

    render() {
        const {
            children, maxWords, prewrapped, ...props
        } = this.props;
        const numWords = children.split(/\s+/).length;

        if (numWords > maxWords) {
            return this.renderCollapsingText();
        }

        return (
            <Text
                data-test="regular-text-section"
                prewrapped={prewrapped}
            >
                <span {...filterToValidAttributes(props)}>
                    {children}
                </span>
            </Text>
        );
    }

    renderHelperButton(text) {
        return (
            <Toggle
                data-test="collapse-words"
                onClick={this.toggleAction}
            >
                { typeof text === 'string' ? text : (<FormattedMessage {...text} />) }
            </Toggle>
        );
    }

    renderHelperText() {
        const { collapse } = this.state;
        const { showLessEnabled, showMoreHelperText, showLessHelperText } = this.props;
        const text = collapse ? showMoreHelperText : showLessHelperText;

        const helperButton = collapse || showLessEnabled ? this.renderHelperButton(text) : null;

        return helperButton;
    }

    renderCollapsingText() {
        const { children, prewrapped, ...props } = this.props;
        const { collapse } = this.state;

        if (collapse) {
            return (
                <Text
                    data-test="collapsed-section"
                    prewrapped={prewrapped}
                >
                    {this.buildCollapsedText()}
                    {this.renderHelperText()}
                </Text>
            );
        }

        return (
            <Text
                data-test="open-section"
                prewrapped={prewrapped}
            >
                <span {...filterToValidAttributes(props)}>{children}</span>
                {this.renderHelperText()}
            </Text>
        );
    }

    buildCollapsedText() {
        const {
            children, collapseWords, prewrapped, ...props
        } = this.props;
        const possiblePreview = prewrapped ? children.split(/([\r\n]+)/)[0] : children;
        const maxCharacters = collapseWords * 5;
        const textArray = possiblePreview.split(/\s+/);
        const textBuilder = [];
        let letters = 0;
        let words = 0;

        while (words < textArray.length && letters <= maxCharacters && words <= collapseWords) {
            const word = textArray[words];
            textBuilder.push(word);
            letters += word.length;
            words++;
        }

        return (
            <span {...filterToValidAttributes(props)}>
                <Preview data-test="preview-span">{textBuilder.join(' ')}</Preview>
                <Hidden>{textArray.slice(words).join(' ')}</Hidden>
            </span>
        );
    }

    toggleAction = () => this.setState(state => ({ collapse: !state.collapse }), () => {
        if (!this.state.collapse) {
            this.props.onClickShowMore();
        }
    })
}

const Text = styled.div`
    color: ${p => p.theme.components.CollapsibleText.textColor};
    white-space: ${p => p.prewrapped ? 'pre-line' : 'normal'};
`;

const Toggle = styled.button`
    background: none;
    border: 0;
    border-bottom: 1px dotted;
    color: ${p => p.theme.components.CollapsibleText.linkColor};
    cursor: pointer;
    font: inherit;
    line-height: ${LINE_HEIGHT};
    margin-left: 4px;
    padding: 0;

    &:focus,
    &:hover {
        border-bottom-style: solid;
    }
`;

const Preview = styled.span`
    &:after {
        content: '... ';
    }
`;

const Hidden = styled.span`
    display: none;
`;
